import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'corpcraft_cart_v1';

const CartContext = createContext(null);

/**
 * Cart catalog — keep in sync with backend services/pricing_service.py.
 * The single source of truth for prices is the backend; this is just the UX
 * label/icon hint plus seed pricing for the cart UI before quote() responds.
 */
export const CART_CATALOG = {
  // subscription tiers (max 1 per cart)
  starter:        { id: 'starter',        name: 'Starter Plan',        type: 'tier', price: 99,  priceUsdc: 91,  billing: '/month' },
  professional:   { id: 'professional',   name: 'Professional Plan',   type: 'tier', price: 299, priceUsdc: 275, billing: '/month' },
  enterprise:     { id: 'enterprise',     name: 'Enterprise Plan',     type: 'tier', price: 999, priceUsdc: 919, billing: '/month' },
  // add-ons
  certification:        { id: 'certification',        name: 'CorpCraft Certification',     type: 'addon', price: 99,  priceUsdc: 91 },
  blockchain_anchor:    { id: 'blockchain_anchor',    name: 'Blockchain Anchor',           type: 'addon', price: 29,  priceUsdc: 27 },
  email_signing_pack:   { id: 'email_signing_pack',   name: 'Email Signing Pack (5)',      type: 'addon', price: 49,  priceUsdc: 45 },
  team_seat:            { id: 'team_seat',            name: 'Team Seat',                   type: 'addon', price: 25,  priceUsdc: 23, billing: '/month' },
  trademark_filing:     { id: 'trademark_filing',     name: 'USPTO Trademark Filing',      type: 'addon', price: 500, priceUsdc: 460 },
  trademark_deep_scan:  { id: 'trademark_deep_scan',  name: 'Deep-Scan Trademark Clearance', type: 'addon', price: 49,  priceUsdc: 45 },
  // dynamic-priced line items (price computed server-side from entity)
  state_filing_fee: { id: 'state_filing_fee', name: 'State Filing Fee', type: 'state_filing', dynamic: true },
  formation_package: { id: 'formation_package', name: 'Formation Package', type: 'formation_package', dynamic: true },
};

const TIER_IDS = new Set(['starter', 'professional', 'enterprise']);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* quota */ }
  }, [items]);

  const addItem = useCallback((serviceId, quantity = 1, options = {}) => {
    if (!CART_CATALOG[serviceId]) return;
    const { entityId, entityName, state, priceUsd, priceUsdc, formationInputs, entityCredits, label, aLaCarteSavings } = options;
    setItems((prev) => {
      // If adding a subscription tier, replace any existing tier (max 1 per cart)
      const isTier = TIER_IDS.has(serviceId);
      let next = prev;
      if (isTier) {
        next = prev.filter((it) => !TIER_IDS.has(it.service_id));
      }

      // Formation packages — only one per cart, replace previous if re-configured
      if (serviceId === 'formation_package') {
        next = next.filter((it) => it.service_id !== 'formation_package');
        return [...next, {
          service_id: serviceId,
          quantity: 1,
          formation_inputs: formationInputs,
          price_usd: priceUsd,
          price_usdc: priceUsdc,
          entity_credits: entityCredits,
          a_la_carte_savings_usd: aLaCarteSavings || 0,
          label: label || 'Formation Package',
          dynamic: true,
        }];
      }

      // For dynamic items keyed by entity (state_filing_fee), de-dupe by entity_id
      if (serviceId === 'state_filing_fee' && entityId) {
        if (next.some((it) => it.service_id === serviceId && it.entity_id === entityId)) {
          return next; // already in cart
        }
        return [...next, {
          service_id: serviceId,
          quantity: 1,
          entity_id: entityId,
          entity_name: entityName,
          state,
          price_usd: priceUsd,
          price_usdc: priceUsdc,
          dynamic: true,
        }];
      }

      const existing = next.find((it) => it.service_id === serviceId);
      if (existing) {
        if (isTier) return next; // tier qty stays at 1
        return next.map((it) =>
          it.service_id === serviceId ? { ...it, quantity: Math.min(100, it.quantity + quantity) } : it,
        );
      }
      return [...next, { service_id: serviceId, quantity: isTier ? 1 : quantity }];
    });
  }, []);

  const removeItem = useCallback((serviceId, entityId = null) => {
    setItems((prev) => prev.filter((it) => {
      if (it.service_id !== serviceId) return true;
      if (entityId && it.entity_id !== entityId) return true;
      return false;
    }));
  }, []);

  const updateQuantity = useCallback((serviceId, quantity) => {
    if (TIER_IDS.has(serviceId)) return; // tiers always qty 1
    if (quantity <= 0) {
      setItems((prev) => prev.filter((it) => it.service_id !== serviceId));
      return;
    }
    setItems((prev) =>
      prev.map((it) => (it.service_id === serviceId ? { ...it, quantity: Math.min(100, quantity) } : it)),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    let totalUsd = 0;
    let totalUsdc = 0;
    let count = 0;
    for (const it of items) {
      const cat = CART_CATALOG[it.service_id];
      if (!cat) continue;
      // Dynamic-priced items carry their own price on the line item
      const priceUsd = cat.dynamic ? (it.price_usd || 0) : cat.price;
      const priceUsdc = cat.dynamic ? (it.price_usdc || 0) : cat.priceUsdc;
      totalUsd += priceUsd * it.quantity;
      totalUsdc += priceUsdc * it.quantity;
      count += it.quantity;
    }
    return {
      totalUsd: Number(totalUsd.toFixed(2)),
      totalUsdc: Number(totalUsdc.toFixed(2)),
      savingsUsd: Number((totalUsd - totalUsdc).toFixed(2)),
      itemCount: count,
      tierInCart: items.find((it) => TIER_IDS.has(it.service_id))?.service_id || null,
    };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQuantity, clear, totals }),
    [items, addItem, removeItem, updateQuantity, clear, totals],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
